import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import ChapterDiagrams from "../components/ChapterDiagrams";
import ChapterHero from "../components/ChapterHero";
import {
  BookOpen, Layers, Stethoscope, Sparkles, Printer, ChevronRight,
  CheckCircle2, ListChecks,
} from "lucide-react";

/**
 * Exam Review Pack — aggregates every cached piece of material into one
 * scrollable page. Zero new LLM cost: pulls lesson/summary/flashcards/scenario
 * content from cache. Designed to double as a print-to-PDF revision sheet.
 */
export default function Review() {
  const { user, loading } = useAuth();
  const { lang, t, isRTL } = useLanguage();
  const navigate = useNavigate();

  const [chapters, setChapters] = useState([]);
  const [byChapter, setByChapter] = useState({}); // { chapterId: { summary, flashcards, scenario } }
  const [loadingPack, setLoadingPack] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    else if (!loading && user && user.status === "pending") navigate("/pending", { replace: true });
  }, [loading, user, navigate]);

  // Fetch chapters once, then content in parallel.
  useEffect(() => {
    if (!user || user.status === "pending") return;
    let cancelled = false;
    (async () => {
      setLoadingPack(true);
      try {
        const r = await api.get("/chapters");
        const chs = r.data.sort((a, b) => a.number - b.number);
        if (cancelled) return;
        setChapters(chs);

        const kinds = ["summary", "flashcards", "scenario"];
        const entries = await Promise.all(
          chs.flatMap((ch) =>
            kinds.map((k) =>
              api.get(`/chapters/${ch.id}/content/${k}`, { params: { lang } })
                .then((res) => [ch.id, k, res.data])
                .catch(() => [ch.id, k, null]),
            ),
          ),
        );
        if (cancelled) return;
        const grouped = {};
        entries.forEach(([cid, kind, data]) => {
          grouped[cid] = grouped[cid] || {};
          grouped[cid][kind] = data;
        });
        setByChapter(grouped);
      } finally {
        if (!cancelled) setLoadingPack(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, lang]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-bone">
      <div className="print:hidden">
        <Header />
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        {/* Title + print button */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-1">
              {isRTL ? "حزمة المراجعة" : "Revision pack"}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              {isRTL ? "كل ما تحتاجه قبل الامتحان" : "Everything you need before the exam"}
            </h1>
            <p className="mt-2 text-muted-foreground max-w-2xl leading-relaxed">
              {isRTL
                ? "ملخصات، رسومات، بطاقات مفاتيح، ولآلئ إكلينيكية لكل الفصول في صفحة واحدة."
                : "Summaries, diagrams, flashcards and clinical pearls for every chapter — one scroll."}
            </p>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="print-review-pack"
            className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-white font-medium px-4 py-2.5 rounded-xl text-sm transition print:hidden"
          >
            <Printer className="w-4 h-4" /> {isRTL ? "طباعة / PDF" : "Print / save PDF"}
          </button>
        </div>

        {/* Chapter quick jump */}
        {chapters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10 print:hidden" data-testid="chapter-jump">
            {chapters.map((ch) => (
              <a
                key={ch.id}
                href={`#ch-${ch.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-border hover:bg-sage/5 hover:border-sage/30 text-xs font-medium text-muted-foreground hover:text-sage transition"
              >
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sage/10 text-sage text-[10px] font-bold">
                  {ch.number}
                </span>
                {ch.title}
                <ChevronRight className="w-3 h-3" />
              </a>
            ))}
          </div>
        )}

        {loadingPack && (
          <div className="py-20 text-center text-muted-foreground text-sm" data-testid="review-loading">
            {isRTL ? "جارٍ تجميع المواد..." : "Compiling every cached piece of material…"}
          </div>
        )}

        {!loadingPack && chapters.map((ch) => (
          <ChapterReviewSection
            key={ch.id}
            chapter={ch}
            content={byChapter[ch.id] || {}}
            lang={lang}
            isRTL={isRTL}
          />
        ))}

        {!loadingPack && chapters.length > 0 && (
          <div className="mt-16 rounded-2xl bg-sage text-white p-8 text-center print:hidden">
            <Sparkles className="w-6 h-6 mx-auto mb-3 opacity-80" />
            <div className="font-display text-xl font-semibold mb-1">
              {isRTL ? "بالتوفيق!" : "You've got this."}
            </div>
            <div className="text-sm opacity-90">
              {isRTL
                ? "تذكر أن المراجعة الفعّالة قصيرة ومتكررة."
                : "Short, spaced reviews beat long marathon sessions."}
            </div>
          </div>
        )}
      </main>

      {/* Print rules */}
      <style>{`
        @media print {
          body { background: white !important; }
          .bg-bone { background: white !important; }
          .shadow-sm, .shadow-md { box-shadow: none !important; }
          .review-section { break-inside: avoid; page-break-inside: avoid; }
          a { text-decoration: none; color: inherit; }
        }
      `}</style>
    </div>
  );
}

function ChapterReviewSection({ chapter, content, lang, isRTL }) {
  const summary = content.summary;
  const flashcards = content.flashcards;
  const scenario = content.scenario;

  // Collect scenarios whether new array-format or legacy single.
  const scenarios = useMemo(() => {
    if (!scenario) return [];
    if (Array.isArray(scenario.scenarios)) return scenario.scenarios;
    if (scenario.title) return [scenario];
    return [];
  }, [scenario]);

  // Take first 10 flashcards for a compressed revision slice.
  const cards = (flashcards?.cards || []).slice(0, 10);

  return (
    <section
      id={`ch-${chapter.id}`}
      className="review-section scroll-mt-24 mb-14"
      data-testid={`review-chapter-${chapter.id}`}
    >
      {/* Chapter header */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center text-white font-display text-lg font-bold shrink-0"
          style={{ background: chapter.gradient ? undefined : "#4A675C", backgroundImage: chapter.gradient }}
        >
          {chapter.number}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            {isRTL ? `الفصل ${chapter.number}` : `Chapter ${chapter.number}`}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{chapter.title}</h2>
        </div>
      </div>

      <ChapterHero chapterId={chapter.id} title={chapter.title} />

      {/* Summary block */}
      {summary && (
        <div className="rounded-2xl border border-border bg-white p-5 sm:p-6 mb-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-3">
            <BookOpen className="w-3.5 h-3.5" />
            {isRTL ? "الملخص" : "Overview"}
          </div>
          {summary.overview && (
            <p className="text-sm leading-relaxed text-foreground/90">{summary.overview}</p>
          )}
          {summary.must_remember?.length > 0 && (
            <div className="mt-4 rounded-xl bg-bone border border-border p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta font-bold mb-2">
                <ListChecks className="w-3 h-3 inline-block me-1" />
                {isRTL ? "يجب تذكّره" : "Must remember"}
              </div>
              <ul className="space-y-1.5">
                {summary.must_remember.map((item, i) => (
                  <li key={`mr-${i}-${item.slice(0, 20)}`} className="flex items-start gap-2 text-sm leading-snug">
                    <CheckCircle2 className="w-3.5 h-3.5 text-terracotta mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary.exam_tips?.length > 0 && (
            <div className="mt-3 rounded-xl bg-sage/5 border border-sage/30 p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-2">
                <Sparkles className="w-3 h-3 inline-block me-1" />
                {isRTL ? "نصائح للامتحان" : "Exam tips"}
              </div>
              <ul className="space-y-1.5">
                {summary.exam_tips.map((item, i) => (
                  <li key={`et-${i}-${item.slice(0, 20)}`} className="flex items-start gap-2 text-sm leading-snug">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Diagrams */}
      <ChapterDiagrams chapterId={chapter.id} lang={lang} />

      {/* Flashcards compressed */}
      {cards.length > 0 && (
        <div className="mt-5 rounded-2xl border border-border bg-white p-5 sm:p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-3">
            <Layers className="w-3.5 h-3.5" />
            {isRTL ? "بطاقات المراجعة السريعة" : "Quick flashcards"} · {cards.length}/{flashcards.cards.length}
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {cards.map((c, i) => (
              <div
                key={`${chapter.id}-card-${i}`}
                className="rounded-xl border border-border bg-bone p-3"
                data-testid={`review-card-${chapter.id}-${i}`}
              >
                <div className="text-xs font-semibold text-sage leading-snug">{c.front}</div>
                <div className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{c.back}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scenario teaching points */}
      {scenarios.length > 0 && (
        <div className="mt-5 rounded-2xl border border-terracotta/30 bg-gradient-to-br from-terracotta/5 to-sage/5 p-5 sm:p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-terracotta font-bold mb-3">
            <Stethoscope className="w-3.5 h-3.5" />
            {isRTL ? "خلاصات سريرية من السيناريوهات" : "Scenario teaching points"}
          </div>
          <div className="space-y-3">
            {scenarios.map((s, si) => (
              <div key={`sc-${si}-${(s.title || "").slice(0, 20)}`} className="rounded-xl bg-white border border-border p-4">
                <div className="text-xs font-semibold">{s.title}</div>
                {s.setting && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">{s.setting}</div>
                )}
                {s.teaching_points?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {s.teaching_points.map((tp, i) => (
                      <li key={`tp-${si}-${i}`} className="flex items-start gap-2 text-xs leading-relaxed">
                        <span className="mt-1 w-1 h-1 rounded-full bg-terracotta shrink-0" />
                        <span>{tp}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
